import { Fragment, useCallback } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  PresentationChartBarIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  ClipboardDocumentListIcon,
  Bars4Icon,
  UserIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import { IMAGES } from '@/assets';
import classNames from '@/helpers/classNames';
import { useDispatch, useSelector } from '@/store';
import { userSelector, userTypeSelector } from '@/store/selectors/auth';
import { Link, useLocation } from 'react-router-dom';
import { logout } from '@/store/slices/auth';
import Notifications from './Notifications';
import { clearImpersonatedUid, isImpersonatingUser } from '@/helpers/impersonation';

type MenuItem = {
  name: string;
  icon?: React.ReactNode;
  href: string;
  subRoutes?: MenuItem[];
};

const vendorMenuItems: MenuItem[] = [
  {
    name: 'Inspections',
    href: '/',
    icon: <ClipboardDocumentCheckIcon className="h-5 w-5" aria-hidden="true" />
  }
];

const menuItems: MenuItem[] = [
  ...vendorMenuItems,
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: <PresentationChartBarIcon className="h-5 w-5" aria-hidden="true" />
  },
  {
    name: 'Settings',
    href: '/settings/users',
    icon: <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />,
    subRoutes: [
      {
        name: 'Users',
        href: '/settings/users',
        icon: <UserIcon className="h-5 w-5" aria-hidden="true" />
      },
      {
        name: 'Factories',
        href: '/settings/factories',
        icon: <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
      },
      {
        name: 'Clients',
        href: '/settings/clients',
        icon: <UserGroupIcon className="h-5 w-5" aria-hidden="true" />
      },
      {
        name: 'Checklist templates',
        href: '/settings/checklist-templates',
        icon: <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden="true" />
      },
      {
        name: 'AQL configurations',
        href: '/settings/aql-configurations',
        icon: <Bars4Icon className="h-5 w-5" aria-hidden="true" />
      }
    ]
  }
];

export default function Navbar() {
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const onSignOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const onStopImpersonation = useCallback(() => {
    clearImpersonatedUid();
    window.location.reload();
  }, []);

  const renderMenuItem = useCallback(
    (item: MenuItem) => {
      if (item.subRoutes) {
        return (
          <Menu as="div" className="relative inline-block text-left" key={item.name}>
            {({ open }) => (
              <>
                <div>
                  <Menu.Button
                    className={classNames(
                      item.subRoutes?.some((s) => s.href === pathname) ? 'bg-gray-100' : '',
                      'inline-flex px-4 py-2 align-middle text-sm text-gray-700 hover:bg-gray-100'
                    )}>
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.name}
                    <ChevronDownIcon
                      className="ml-2 -mr-1 h-5 w-5 text-gray-700 hover:text-primary"
                      aria-hidden="true"
                    />
                  </Menu.Button>
                </div>

                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95">
                  <Menu.Items
                    static
                    className="absolute left-0 z-40 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {item?.subRoutes?.map((subItem) => (
                        <Menu.Item key={subItem.name}>
                          <Link
                            to={subItem.href}
                            className={classNames(
                              pathname === subItem.href
                                ? 'bg-primary text-white hover:opacity-80'
                                : 'text-gray-700  hover:bg-gray-100',
                              'inline-flex w-full rounded-md px-4 py-2 align-middle text-sm'
                            )}>
                            {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                            {subItem.name}
                          </Link>
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        );
      }
      return (
        <Link
          key={item.name}
          to={item.href}
          className={classNames(
            pathname === item.href
              ? 'bg-primary text-white'
              : 'text-gray-500 hover:bg-primary hover:text-white',
            'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium'
          )}
          aria-current={pathname === item.href ? 'page' : undefined}>
          {item.icon && <span className="mr-2">{item.icon}</span>}
          {item.name}
        </Link>
      );
    },
    [pathname]
  );

  const renderMenuItemMobile = useCallback(
    (item: MenuItem) => (
      <Fragment key={item.name}>
        <Disclosure.Button
          key={item.name}
          as="a"
          href={item.href}
          className={classNames(
            pathname === item.href
              ? 'bg-primary text-white'
              : 'text-gray-800 hover:bg-primary hover:text-white',
            'inline-flex w-full items-center rounded-md px-3 py-2 text-base font-medium'
          )}
          aria-current={pathname === item.href ? 'page' : undefined}>
          {item.icon && <span className="mr-2">{item.icon}</span>}
          {item.name}
          {item.subRoutes && (
            <ChevronDownIcon
              className={classNames(
                (
                  item.subRoutes
                    ? item.subRoutes.some((s) => s.href === pathname)
                    : pathname === item.href
                )
                  ? 'text-white'
                  : 'text-gray-800 hover:text-white',
                'ml-2 h-5 w-5'
              )}
              aria-hidden="true"
            />
          )}
        </Disclosure.Button>
        {item.subRoutes && (
          <Disclosure.Panel
            className={classNames(
              'space-y-1 rounded-md pl-4 pt-2 pb-3',
              item.subRoutes.some((s) => s.href === pathname) ? 'bg-gray-200' : ''
            )}>
            {item.subRoutes.map((subItem) => (
              <Link
                key={subItem.name}
                to={subItem.href}
                className={classNames(
                  pathname === subItem.href
                    ? 'bg-primary text-white'
                    : 'text-gray-800 hover:bg-primary hover:text-white',
                  'flex flex-row rounded-md px-3 py-2 text-base font-medium'
                )}
                aria-current={pathname === subItem.href ? 'page' : undefined}>
                {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                {subItem.name}
              </Link>
            ))}
          </Disclosure.Panel>
        )}
      </Fragment>
    ),
    [pathname]
  );

  const userType = useSelector(userTypeSelector);
  const isImpersonating = isImpersonatingUser(user?.uid);

  return (
    <Disclosure
      as="nav"
      className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm sm:px-2"
      style={{ zIndex: 500 }}>
      {({ open }) => (
        <>
          <div className="w-full">
            <div className="container relative mx-auto flex h-16 items-center justify-between px-4">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                {/* Mobile menu button*/}
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex flex-shrink-0 items-center">
                  <img
                    className="block h-10 w-auto lg:hidden"
                    src={IMAGES.logo}
                    alt="Your Company"
                  />
                  <img
                    className="hidden h-10 w-auto lg:block"
                    src={IMAGES.logo}
                    alt="Your Company"
                  />
                </div>
                <div className="hidden sm:ml-6 sm:block">
                  <div className="flex space-x-4">
                    {(userType === 'admin' ? menuItems : vendorMenuItems).map(renderMenuItem)}
                  </div>
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                <Notifications />
                <a
                  href="https://forms.gle/xTPUaUMYvLkZZCfNA"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Segnala un problema"
                  className="ml-3 rounded-full p-1 text-gray-500 hover:text-primary focus:outline-none">
                  <FlagIcon className="h-4 w-4" aria-hidden="true" />
                </a>
                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button
                      className="flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
                      data-testid="user-menu">
                      <span className="ml-2 mr-4 hidden sm:block">
                        Hi, {user?.userInfo?.firstName || user?.email}
                      </span>
                      <img
                        className="h-8 w-8 rounded-full"
                        src={user?.userInfo?.profileImage || IMAGES.userPlaceholder}
                        alt=""
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95">
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        <Link
                          to="/profile"
                          data-testid="profile-btn"
                          className={classNames(
                            pathname === '/profile' ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700',
                            'cursor-pointer hover:bg-gray-100'
                          )}>
                          Your Profile
                        </Link>
                      </Menu.Item>

                      {isImpersonating ? (
                        <Menu.Item>
                          <div
                            className="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            data-testid="stop-impersonation-btn"
                            onClick={onStopImpersonation}>
                            Stop impersonating
                          </div>
                        </Menu.Item>
                      ) : null}

                      <Menu.Item>
                        <div
                          className="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          data-testid="logout-btn"
                          onClick={onSignOut}>
                          Sign out
                        </div>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden">
            <div className="space-y-1 px-2 pt-2 pb-3">
              {(userType === 'admin' ? menuItems : vendorMenuItems).map(renderMenuItemMobile)}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
